/* eslint-disable no-undef */
import { Environment, TestEnv, MIN_IOS_VERSION, MIN_SAFARI_VERSION } from 'lib/constants';
import UAParser from 'ua-parser-js';

const { LOCALDEV, SANDBOX, CI, DEV, STAGING, PROD } = Environment;

const parser = new UAParser().setUA(global.navigator.userAgent);
const uaProfile = parser.getResult();

const { os, browser, device } = uaProfile;

const isIpad = () => {
  const ua = navigator.userAgent;
  const p = navigator.platform;
  const ipad = ua.indexOf('iPad') > -1;
  if (/iPad/.test(p) || ipad) {
    return true;
  }
  return (
    navigator.maxTouchPoints &&
    navigator.maxTouchPoints > 2 &&
    /MacIntel/.test(p)
  );
};

/**
 * Converts the given version string into a major.minor float.
 * Examples:
 * parseVersion('76.1.3809.132') -> 76.1
 * parseVersion('11.1') -> 11.1
 * parseVersion('11') -> 11
 * parseVersion('10.0') -> 10
 */
export const parseVersion = (version) => {
  const regexExtract = /\d+\.\d+/.exec(version);
  if (regexExtract && regexExtract.length > 0) {
    return parseFloat(regexExtract[0]);
  }
  return parseFloat(version);
};

const deviceDetails = {
  os: {
    name:
      window.orientation !== undefined && os.name === 'Mac OS'
        ? 'iOS'
        : os.name,
    version: parseFloat(os.version),
    versionString: os.version,
    parsedVersion: parseVersion(os.version),
  },
  browser: {
    name: browser.name,
    version: browser.version,
    majorVersion: browser.version.split('.')[0],
    parsedVersion: parseVersion(browser.version),
  },
  device: {
    type: device.type,
    vendor: device.vendor,
    model: device.model,
  },
  get isOldIPad() {
    return this.os.name === 'iOS' && this.os.version < 11;
  },
  get isSafari() {
    return this.browser.name.toLowerCase().includes('safari');
  },
  get isIOS() {
    return this.os.name === 'iOS';
  },
};

/**
 * Checks if the current device is running Aqua on a browser on a mobile device.
 */
const isMobile = () => {
  const osName = deviceDetails.os.name;
  if (
    osName &&
    (osName.name === 'Mac OS' ||
      osName.name === 'Windows' ||
      osName.name === 'Chromium OS' ||
      osName.name === 'Linux')
  ) {
    return false;
  }
  return true;
};

const isDeviceSupported = () => {
  const browserName = deviceDetails.browser.name;
  const browserVersion = deviceDetails.browser.parsedVersion;
  const deviceModel = deviceDetails.device.model;
  const osType = deviceDetails.os.name;

  if (deviceModel) {
    if (deviceModel === 'iPad' || deviceModel === 'iPhone') {
      // For devices: iPad, iOS 10.3+, Safari 10.3+
      // (setting minimum to 10.3 due to LAUSD having a significant ammount of iPads at this version)
      // iPad uses Mac Desktop Agent for user agent string (enabled by default)
      // (because Safari is part of the iOS operating system, its version is the same as the iOS on iPad/iPhone)
      if (
        (browserVersion < MIN_IOS_VERSION) ||
        !(browserName === 'Mobile Safari' || browserName === 'Safari')
      ) {
        return false;
      }
    }
  } else if (osType === 'Mac OS') {
    // For laptops/desktops:
    // - Windows 7+, Chrome
    // - Chromebook, Chrome
    // - Mac, Safari 11+ or Chrome
    if (browserName !== 'Chrome' && browserName !== 'Safari') {
      return false;
    }
    if (browserName === 'Safari' && browserVersion < MIN_SAFARI_VERSION) {
      return false;
    }
  } else if (browserName !== 'Chrome') {
    return false;
  } else if (
    // make an exception for Linux, because that's what the Selenium grid uses for tests
    osType !== 'Windows' &&
    osType !== 'Chromium OS' &&
    osType !== 'Linux'
  ) {
    return false;
  }

  return true;
};

const Env =
  process.env.NODE_ENV === 'test'
    ? { ...TestEnv, deviceDetails }
    : {
      ...EnvSettings,
      SOURCE_COMMIT_ID: AQUA_GLOBALS.SOURCE_COMMIT_ID, // global defined in web/index.html
      deviceDetails,
      isIpad: isIpad(),
      isMobile: isMobile(),
      isDeviceSupported: isDeviceSupported(),
      // calc values once ahead of time and put booleans in here for easy reference later
      isLocalDev: EnvSettings.ENVIRONMENT === LOCALDEV,
      isSandbox: EnvSettings.ENVIRONMENT === SANDBOX,
      isCI: EnvSettings.ENVIRONMENT === CI,
      isDev: EnvSettings.ENVIRONMENT === DEV,
      isStaging: EnvSettings.ENVIRONMENT === STAGING,
      isProd: EnvSettings.ENVIRONMENT === PROD,
      isDevEnv: [LOCALDEV, SANDBOX, CI, DEV].includes(
        EnvSettings.ENVIRONMENT,
      ),
      isReleaseEnv: [PROD, STAGING].includes(EnvSettings.ENVIRONMENT),
    };

export default Env;
